import { useState } from "react";

import { EmptystateChartColored } from "components/icons/generated";
import Box from "ds/components/Box";
import Card from "ds/components/Card";
import EmptyState from "ds/components/EmptyState";
import TabWithCounter from "ds/components/Tab/WithCounter";
import TabPanel from "ds/components/TabPanel";

import RunObservabilitySlowestItemsCardRow from "./Row";
import styles from "./styles.module.css";
import { SlowestItem, SlowestItemGroup } from "./types";

type RunObservabilitySlowestItemsCardProps = {
  title: string;
  emptyTitle: string;
  emptyPhaseTitle?: string;
  groups: SlowestItemGroup[];
  scaleSeconds: number;
};

const getWidthPercent = (seconds: number, scaleSeconds: number) =>
  scaleSeconds > 0 ? (seconds / scaleSeconds) * 100 : 0;

const hasItems = ({ items }: SlowestItemGroup) => items.length > 0;

const getDefaultGroup = (groups: SlowestItemGroup[]) =>
  groups.findLast(hasItems) ?? groups[groups.length - 1];

const renderItems = (items: SlowestItem[], scaleSeconds: number) =>
  items.map((item, index) => (
    <RunObservabilitySlowestItemsCardRow
      key={index}
      {...item}
      widthPercent={getWidthPercent(item.seconds, scaleSeconds)}
    />
  ));

const RunObservabilitySlowestItemsCard = ({
  title,
  emptyTitle,
  emptyPhaseTitle,
  groups,
  scaleSeconds,
}: RunObservabilitySlowestItemsCardProps) => {
  const [activeId, setActiveId] = useState(() => getDefaultGroup(groups)?.id);

  const activeGroup = groups.find(({ id }) => id === activeId) ?? getDefaultGroup(groups);
  const hasTabs = groups.length > 1;
  const phase = hasTabs ? undefined : groups[0]?.label;

  const renderGroup = (group: SlowestItemGroup) =>
    hasItems(group) ? (
      renderItems(group.items, scaleSeconds)
    ) : (
      <EmptyState icon={EmptystateChartColored} title={emptyPhaseTitle ?? emptyTitle} />
    );

  return (
    <Card
      title={phase ? `${title} while ${phase.toLowerCase()}` : title}
      titleTag="h3"
      contentGap="medium"
      contentClassName={styles.content}
      zeroMinWidth
    >
      {groups.length === 0 && <EmptyState icon={EmptystateChartColored} title={emptyTitle} />}

      {!hasTabs && activeGroup && (
        <Box direction="column" fullWidth grow="1" className={styles.rows}>
          {renderGroup(activeGroup)}
        </Box>
      )}

      {hasTabs && (
        <Box
          direction="row"
          align="center"
          gap="small"
          padding="small 0 0 0"
          role="tablist"
          aria-label={title}
        >
          {groups.map(({ id, label, items }) => (
            <TabWithCounter
              key={id}
              id={id}
              size="small"
              isActive={id === activeGroup.id}
              onClick={setActiveId}
              label={label}
              count={items.length}
            />
          ))}
        </Box>
      )}

      {hasTabs &&
        groups.map((group) => (
          <TabPanel
            key={group.id}
            id={group.id}
            isActive={group.id === activeGroup.id}
            fullWidth
            grow="1"
            className={styles.rows}
          >
            {renderGroup(group)}
          </TabPanel>
        ))}
    </Card>
  );
};

export default RunObservabilitySlowestItemsCard;
